import React from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const rootSx = {
  width: "100%",
  maxWidth: "960px",
  mx: "auto",
  boxSizing: "border-box",
  color: "text.main",
  fontSize: "1.125rem",
  "& button": {
    minHeight: "44px !important",
    padding: "8px 12px !important",
    fontSize: "1rem !important",
    lineHeight: "1.2 !important",
    whiteSpace: "normal",
  },
  "& .individual-editor-action": {
    backgroundColor: "#d9d9d9 !important",
    color: "#000 !important",
    border: "1px solid #000 !important",
  },
  "& .individual-editor-action:disabled": {
    backgroundColor: "#c9c9c9 !important",
    color: "#555 !important",
    borderColor: "#777 !important",
    cursor: "not-allowed !important",
  },
  "& .individual-editor-action:focus-visible": {
    outline: "3px solid #1976d2",
    outlineOffset: "2px",
  },
  "& input": {
    minWidth: 0,
    maxWidth: "100%",
    fontSize: "1.125rem !important",
    fontVariantNumeric: "tabular-nums",
  },
  "& textarea": {
    minHeight: "48px !important",
    fontSize: "1.125rem !important",
    textAlign: "left",
  },
  "& textarea:focus": { minHeight: "96px !important" },
};

const sectionSx = {
  px: { xs: 1, sm: 2 },
  py: 1.5,
};

const sideSx = {
  minWidth: 0,
  px: { xs: 0.5, sm: 1.5 },
  py: 1.25,
  backgroundColor: "rgba(248, 250, 255, 0.72)",
  "& > div": { width: "100%" },
};

function SectionTitle({ children }) {
  return (
    <MDTypography
      variant="h6"
      color="text"
      textAlign="center"
      sx={{ mb: 1.25, fontSize: "1.25rem" }}
    >
      {children}
    </MDTypography>
  );
}

SectionTitle.propTypes = { children: PropTypes.node.isRequired };

function SideSection({ title, children }) {
  return (
    <MDBox sx={sideSx}>
      <MDTypography
        variant="button"
        fontWeight="bold"
        display="block"
        textAlign="center"
        sx={{ mb: 1, fontSize: "1.125rem" }}
      >
        {title}
      </MDTypography>
      {children}
    </MDBox>
  );
}

SideSection.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node };

function FullWidthSection({ title, children, maxWidth = 620 }) {
  if (children == null) return null;
  return (
    <MDBox sx={sectionSx}>
      {title && <SectionTitle>{title}</SectionTitle>}
      <MDBox sx={{ width: "100%", maxWidth, mx: "auto" }}>{children}</MDBox>
    </MDBox>
  );
}

FullWidthSection.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  maxWidth: PropTypes.number,
};

function NoteSection({ children }) {
  if (children == null) return null;
  return <MDBox sx={sectionSx}>{children}</MDBox>;
}

NoteSection.propTypes = {
  children: PropTypes.node,
};

export default function IndividualMachineEditorLayout({
  table,
  type,
  rented = false,
  isLoading = false,
}) {
  const rows = Array.isArray(table?.rows) ? table.rows : [];
  const counterRow = rows.find((row) => row?.__isCounterVerificationDetailRow === true) || null;
  const mainRow = rows.find((row) => row?.__isCounterVerificationDetailRow !== true) || null;
  const isSlot =
    String(type || "")
      .toLowerCase()
      .includes("tragamoneda") && !rented;

  if (!mainRow) {
    return (
      <MDBox sx={{ ...rootSx, p: 2, textAlign: "center" }}>
        <MDTypography variant="body2" color="text">
          {isLoading ? "Cargando máquina..." : "No hay información disponible para esta máquina."}
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox className="individual-machine-editor" sx={rootSx}>
      <MDBox
        className="machine-header"
        sx={{
          px: { xs: 1, sm: 2 },
          py: 1.5,
          textAlign: "center",
          "& > div": { width: "100%" },
        }}
      >
        {mainRow.Machine}
      </MDBox>

      {isSlot ? (
        <>
          <MDBox sx={sectionSx}>
            <MDBox
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                gap: { xs: 0.5, sm: 1.5 },
              }}
            >
              <SideSection title="Entrada">{mainRow.In}</SideSection>
              <SideSection title="Salida">{mainRow.Out}</SideSection>
            </MDBox>
          </MDBox>
          <FullWidthSection>{mainRow.Total}</FullWidthSection>
          <NoteSection>{mainRow.Note}</NoteSection>
        </>
      ) : (
        <>
          <FullWidthSection>{mainRow.In}</FullWidthSection>
          {!rented && mainRow.Out != null && <FullWidthSection>{mainRow.Out}</FullWidthSection>}
          {!rented && mainRow.Total != null && <FullWidthSection>{mainRow.Total}</FullWidthSection>}
          <NoteSection>{mainRow.Note}</NoteSection>
        </>
      )}

      {isSlot && counterRow && (
        <MDBox sx={{ borderTop: "1px solid", borderColor: "grey.700" }}>
          <MDBox
            sx={{ ...sectionSx, textAlign: "center", backgroundColor: "rgba(247, 249, 255, 0.92)" }}
          >
            {counterRow.Machine}
          </MDBox>
          <MDBox
            sx={{
              ...sectionSx,
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
              gap: { xs: 0.5, sm: 1.5 },
            }}
          >
            <SideSection title="Entrada">{counterRow.In}</SideSection>
            <SideSection title="Salida">{counterRow.Out}</SideSection>
          </MDBox>
          <NoteSection>{counterRow.Note}</NoteSection>
        </MDBox>
      )}
    </MDBox>
  );
}

IndividualMachineEditorLayout.propTypes = {
  table: PropTypes.shape({ rows: PropTypes.array }).isRequired,
  type: PropTypes.string,
  rented: PropTypes.bool,
  isLoading: PropTypes.bool,
};
